import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { WifiOff } from 'lucide-react';
import { env } from '../../lib/env';
import { registerNativePush } from '../../lib/push';
import { homeFor, useAuth } from '../../stores/auth';
import { useToast } from '../../components/toast';
import { announceNewRequest } from '../driver/NewRequestAlert';

/** Traduce rutas genéricas de notificaciones (/chat/:id) a la sección del rol. */
export function resolveNotificationUrl(url: string | undefined, roleBase: string): string | null {
  if (!url || !url.startsWith('/')) return null;
  if (url.startsWith('/chat/')) return `${roleBase}${url}`;
  return url;
}

/**
 * Integraciones nativas montadas una vez: push (FCM), enlaces profundos,
 * botón atrás de Android, splash y estado de conexión.
 */
export function NativeBridge() {
  const history = useHistory();
  const toast = useToast();
  const { status, user } = useAuth();
  const [offline, setOffline] = useState(false);
  const roleBase = homeFor(user).startsWith('/driver') ? '/driver' : '/app';

  // Estado de red (también en web).
  useEffect(() => {
    void Network.getStatus().then((s) => setOffline(!s.connected));
    const h = Network.addListener('networkStatusChange', (s) => setOffline(!s.connected));
    return () => void h.then((x) => x.remove());
  }, []);

  useEffect(() => {
    if (!env.isNative) return;
    void SplashScreen.hide();
    // Enlaces profundos: https://<dominio>/app/rides/… abre la ruta interna.
    const openUrl = (url: string) => {
      try {
        const u = new URL(url);
        // https://dominio/app/... o tuconductorrd://app/... (host = primer segmento)
        const path = u.protocol === 'tuconductorrd:' ? `/${u.host}${u.pathname}` : u.pathname;
        // Desde el aviso a pantalla completa: mostrar el aviso grande (ya sonó la notificación).
        const alertId = u.searchParams.get('alert') === '1' ? /^\/driver\/requests\/([0-9a-f-]{36})$/.exec(path)?.[1] : undefined;
        if (alertId) {
          history.push('/driver/dashboard');
          setTimeout(() => announceNewRequest(alertId, { silent: true }), 300);
        } else if (path.startsWith('/')) history.push(path + u.search);
      } catch {
        /* URL inválida */
      }
    };
    const deep = CapApp.addListener('appUrlOpen', ({ url }) => openUrl(url));
    // Arranque en frío desde una notificación o enlace.
    void CapApp.getLaunchUrl().then((r) => r?.url && openUrl(r.url));
    const back = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) history.goBack();
      else void CapApp.minimizeApp();
    });
    return () => {
      void deep.then((x) => x.remove());
      void back.then((x) => x.remove());
    };
  }, [history]);

  // Push tras iniciar sesión.
  useEffect(() => {
    if (status !== 'authenticated' || !env.isNative) return;
    void registerNativePush(
      (data) => {
        const target = resolveNotificationUrl(data.url, roleBase);
        if (target) history.push(target);
      },
      (title, data) => {
        // En primer plano Android no muestra la notificación: aviso dentro de la app.
        if (data.requestId && data.action === 'quote') announceNewRequest(data.requestId);
        else if (data.type !== 'chat.message' || !location.pathname.includes(data.conversationId ?? '__')) toast(title);
      },
    );
  }, [status, history, roleBase, toast]);

  if (!offline) return null;
  return (
    <div role="status" className="fixed top-0 inset-x-0 z-[1000] bg-amber-500 text-black text-sm font-medium flex items-center justify-center gap-2 py-1.5 pt-[max(env(safe-area-inset-top),0.375rem)]">
      <WifiOff className="size-4" aria-hidden /> Sin conexión. Los cambios se enviarán al recuperar la señal.
    </div>
  );
}
